import { useState } from 'react';
import { ShieldCheck, ShieldAlert, AlertTriangle, RefreshCw, ChevronDown, Gauge } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import { reportError } from '../../../../lib/errors';
import { refreshMotHistory } from '../../../../services/vrmService';
import type { CustomerVehicle } from '../tabs/types';

/**
 * The MOT record for one vehicle.
 *
 * This is where the intrinsic detail lives — deliberately NOT in the job or
 * customer create flows, which stay minimal. A mechanic booking a car in needs
 * make/model/mileage and nothing else; a mechanic deciding what to quote for
 * wants the last three years of advisories, and comes here to get it.
 *
 * The summary line renders from columns already on the vehicle row, so the
 * common case costs no extra query. The test-by-test history is fetched only
 * when someone opens it.
 */

interface MotDefect {
    id: string;
    type: string | null;
    defect_text: string | null;
    dangerous: boolean | null;
}

interface MotTestRow {
    id: string;
    completed_date: string | null;
    expiry_date: string | null;
    test_result: string | null;
    odometer_value: string | null;
    odometer_unit: string | null;
    odometer_result_type: string | null;
    data_source: string | null;
    location: string | null;
    vehicle_mot_defects: MotDefect[];
}

const fmtDate = (d?: string | null) =>
    d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : null;

const fmtOdometer = (value?: string | null, unit?: string | null) => {
    if (!value) return null;
    const n = Number(value);
    if (!Number.isFinite(n)) return null;
    return `${n.toLocaleString('en-GB')} ${unit === 'KM' ? 'km' : 'mi'}`;
};

/** Days until expiry, or negative when it has already passed. */
const daysUntil = (date: string) =>
    Math.ceil((new Date(date).getTime() - Date.now()) / 86_400_000);

/**
 * Severity buckets for display only. The raw DVSA type is kept in the database
 * — there are nine documented values and they can add more, so this maps rather
 * than replaces.
 */
function defectTone(type: string | null, dangerous: boolean | null) {
    if (dangerous || type === 'DANGEROUS') return 'text-danger border-danger/30 bg-danger/10';
    if (type === 'MAJOR' || type === 'FAIL' || type === 'PRS') return 'text-warning border-warning/30 bg-warning/10';
    if (type === 'ADVISORY') return 'text-gold border-gold/30 bg-gold-fill/10';
    return 'text-fg-3 border-tint/10 bg-tint/5';
}

export function VehicleMotPanel({ vehicle, onRefreshed }: {
    vehicle: CustomerVehicle;
    onRefreshed: () => void;
}) {
    const [open, setOpen] = useState(false);
    const [tests, setTests] = useState<MotTestRow[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [refreshError, setRefreshError] = useState<string | null>(null);

    const loadHistory = async () => {
        if (tests !== null) return;
        setLoading(true);
        const { data, error } = await supabase
            .from('vehicle_mot_tests')
            .select('id, completed_date, expiry_date, test_result, odometer_value, odometer_unit, odometer_result_type, data_source, location, vehicle_mot_defects(id, type, defect_text, dangerous)')
            .eq('vehicle_id', vehicle.id)
            .order('completed_date', { ascending: false });
        if (error) reportError(error, 'vehicle-mot-panel.vehicle_mot_tests');
        setTests((data as MotTestRow[] | null) ?? []);
        setLoading(false);
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        setRefreshError(null);
        const ok = await refreshMotHistory(vehicle.id);
        if (!ok) {
            // Stale history is still on screen and still drives the reminders —
            // say the refresh failed, not that the data is gone.
            setRefreshError('Could not reach DVSA just now. The stored record is unchanged.');
        } else {
            setTests(null);
            onRefreshed();
        }
        setRefreshing(false);
    };

    const expiry = vehicle.mot_expiry ?? null;
    const days = expiry ? daysUntil(expiry) : null;
    const odometer = fmtOdometer(vehicle.mot_last_odometer_value, vehicle.mot_last_odometer_unit);
    const advisories = vehicle.mot_advisory_count ?? 0;

    const statusTone = days === null ? 'bg-tint/5 text-fg-3 border-tint/10'
        : days < 0 ? 'bg-danger/10 text-danger border-danger/30'
        : days <= 30 ? 'bg-gold-fill/10 text-gold border-gold/30'
        : 'bg-success/10 text-success border-success/30';

    const statusLabel = days === null
        ? (vehicle.mot_first_due_date ? `First MOT due ${fmtDate(vehicle.mot_first_due_date)}` : 'No MOT date on record')
        : days < 0 ? `MOT expired ${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} ago`
        : days === 0 ? 'MOT expires today'
        : `MOT valid — ${days} day${days === 1 ? '' : 's'} left`;

    return (
        <div className="mt-3 rounded-lg border border-tint/10 bg-tint/[0.02] overflow-hidden">
            <div className="p-3 flex flex-wrap items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded border ${statusTone}`}>
                    {days !== null && days < 0 ? <ShieldAlert className="w-3.5 h-3.5" /> : <ShieldCheck className="w-3.5 h-3.5" />}
                    {statusLabel}
                </span>

                {expiry && (
                    <span className="text-xs text-fg-4">{fmtDate(expiry)}</span>
                )}

                {odometer && (
                    <span className="inline-flex items-center gap-1 text-xs text-fg-3">
                        <Gauge className="w-3.5 h-3.5 text-fg-4" />
                        {odometer}
                        {/* Never presented as current mileage. This reading is
                            from the last MOT and can be almost a year old. */}
                        <span className="text-fg-4">at MOT {fmtDate(vehicle.mot_last_test_date)}</span>
                    </span>
                )}

                {advisories > 0 && (
                    <span className="text-xs font-bold text-gold">
                        {advisories} advisor{advisories === 1 ? 'y' : 'ies'}
                    </span>
                )}

                <div className="flex-1 min-w-0" />

                <button
                    type="button"
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-3 text-xs font-bold text-fg-3 hover:text-fg transition-colors disabled:opacity-50"
                >
                    <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />
                    {refreshing ? 'Refreshing' : 'Refresh MOT'}
                </button>

                <button
                    type="button"
                    onClick={() => { setOpen(!open); if (!open) void loadHistory(); }}
                    aria-expanded={open}
                    className="shrink-0 inline-flex items-center gap-1.5 min-h-[44px] px-3 text-xs font-bold text-accent hover:underline"
                >
                    {open ? 'Hide history' : 'MOT history'}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
                </button>
            </div>

            {/* Only for a definite 'Yes'. DVSA's 'Unknown' means they hold no
                recall data — rendering that as "no recalls" would be a safety
                claim with nothing behind it. */}
            {vehicle.has_outstanding_recall === 'Yes' && (
                <div className="px-3 pb-3">
                    <div className="rounded-md bg-danger/10 border border-danger/30 p-2.5 flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-danger shrink-0 mt-0.5" />
                        <span className="text-xs font-semibold text-danger">
                            Outstanding manufacturer recall on this vehicle.
                        </span>
                    </div>
                </div>
            )}

            {refreshError && (
                <p className="px-3 pb-3 text-xs text-gold">{refreshError}</p>
            )}

            {open && (
                <div className="border-t border-tint/10 p-3 space-y-3">
                    {loading && <p className="text-xs text-fg-4">Loading MOT history…</p>}

                    {!loading && tests?.length === 0 && (
                        <p className="text-xs text-fg-4">
                            No MOT tests on record. {vehicle.dvsa_last_checked
                                ? 'DVSA has no test history for this registration.'
                                : 'Tap Refresh MOT to fetch it from DVSA.'}
                        </p>
                    )}

                    {/* Cards, not a table. This is read on a phone in a workshop,
                        and a five-column table at 320px is unreadable. */}
                    {tests?.map((test) => (
                        <div key={test.id} className="rounded-lg border border-tint/10 bg-scrim/20 p-3">
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded border ${
                                    test.test_result === 'PASSED'
                                        ? 'bg-success/10 text-success border-success/30'
                                        : 'bg-danger/10 text-danger border-danger/30'
                                }`}>
                                    {test.test_result ?? 'UNKNOWN'}
                                </span>
                                <span className="text-sm font-semibold text-fg">{fmtDate(test.completed_date)}</span>
                                {fmtOdometer(test.odometer_value, test.odometer_unit) && (
                                    <span className="text-xs text-fg-3">
                                        {fmtOdometer(test.odometer_value, test.odometer_unit)}
                                    </span>
                                )}
                                {test.odometer_result_type && test.odometer_result_type !== 'READ' && (
                                    <span className="text-xs text-fg-4">
                                        ({test.odometer_result_type.toLowerCase().replace('_', ' ')})
                                    </span>
                                )}
                                {/* Northern Irish and commercial tests come back
                                    through the same endpoint and are not the same
                                    thing as a GB light-vehicle MOT. */}
                                {test.data_source && test.data_source !== 'DVSA' && (
                                    <span className="text-[10px] font-bold uppercase tracking-wide text-fg-4 border border-tint/10 rounded px-1.5 py-0.5">
                                        {test.data_source}
                                    </span>
                                )}
                            </div>

                            {test.expiry_date && test.test_result === 'PASSED' && (
                                <p className="mt-1 text-xs text-fg-4">Certificate to {fmtDate(test.expiry_date)}</p>
                            )}

                            {test.vehicle_mot_defects?.length > 0 && (
                                <ul className="mt-2 space-y-1.5">
                                    {test.vehicle_mot_defects.map((d) => (
                                        <li key={d.id} className={`text-xs rounded border px-2 py-1.5 ${defectTone(d.type, d.dangerous)}`}>
                                            <span className="font-bold uppercase tracking-wide text-[10px]">
                                                {d.type ?? 'NOTE'}
                                            </span>
                                            <span className="block mt-0.5 text-fg-2">{d.defect_text}</span>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
